/**
 * DynamicGamePage - Loads a game dynamically via the Game Registry.
 *
 * M1: Reads the GameManifest from the registry, creates an analytics
 * session, dynamically imports the matching GameRuntime factory, mounts
 * the game, and wires runtime events into analytics + the backend
 * session API.
 *
 * Route: /game/:slug
 */
import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { EventTracker } from '@adgf/analytics';
import type { EventType } from '@adgf/shared';
import type { GameRuntime } from '@adgf/game-engine';
import { sessionsApi } from '../lib/apiClient';
import { gameRegistry } from '../games/registry';

export default function DynamicGamePage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const manifest = gameRegistry.getBySlug(slug);

  const containerRef = useRef<HTMLDivElement>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!manifest) return;

    let cancelled = false;
    let runtime: GameRuntime | null = null;
    let backendSessionId: string | null = null;

    const tracker = new EventTracker({ enabled: true, debug: false });
    tracker.track('game_open', { gameId: manifest.id });
    tracker.startSession();

    // Fire-and-forget backend session; errors are non-fatal.
    sessionsApi
      .create({
        game_id: manifest.id,
        game_version: manifest.version,
        anonymous_user_id: tracker.getAnonymousUserId(),
      })
      .then((res) => {
        if (cancelled) return;
        backendSessionId = (res.session as { id?: string } | undefined)?.id ?? null;
      })
      .catch(() => {
        /* analytics is best-effort */
      });

    const isLegacy = manifest.entrypoint.startsWith('legacy:');

    if (!isLegacy) {
      gameRegistry
        .loadRuntime(manifest.slug)
        .then((factory) => {
          if (cancelled || !factory || !containerRef.current) return;

          runtime = factory(
            {
              parent: containerRef.current,
              width: 800,
              height: 600,
              anonymousUserId: tracker.getAnonymousUserId(),
              sessionId: tracker.getSessionId(),
              gameId: manifest.id,
              gameVersion: manifest.version,
            },
            {
              onStart: () => tracker.track('game_start', { gameId: manifest.id }),
              onGameOver: (score) => {
                tracker.track('game_over', { gameId: manifest.id, data: { score } });
                if (backendSessionId) {
                  sessionsApi
                    .end(backendSessionId, { score, completed: true })
                    .catch(() => {});
                }
              },
              onEvent: (eventType, data) =>
                tracker.track(eventType as EventType, { gameId: manifest.id, data }),
              onRetry: () => tracker.track('retry', { gameId: manifest.id }),
            },
          );

          runtime.mount();
          runtime.start();
        })
        .catch(() => {
          if (!cancelled) setLoadFailed(true);
        });
    }

    return () => {
      cancelled = true;
      try {
        runtime?.destroy();
      } catch {
        /* cleanup is best-effort */
      }
      if (backendSessionId) {
        sessionsApi
          .end(backendSessionId, { score: runtime?.getScore() ?? 0, completed: true })
          .catch(() => {});
      }
      tracker.endSession();
      tracker.destroy();
    };
  }, [manifest]);

  if (!manifest) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <h2>Game Not Found</h2>
        <p style={{ color: 'var(--color-text-muted)', margin: '16px 0' }}>
          The game &quot;{slug}&quot; does not exist or has not been published.
        </p>
        <Link to="/">Back to Home</Link>
      </div>
    );
  }

  const isLegacy = manifest.entrypoint.startsWith('legacy:');

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Link to="/" style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)' }}>
          &larr; Back to Games
        </Link>
      </div>

      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: '1.75rem', marginBottom: 4 }}>{manifest.title}</h2>
        <p style={{ color: 'var(--color-text-muted)', marginBottom: 12 }}>{manifest.description}</p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: '0.75rem',
              padding: '2px 8px',
              borderRadius: 4,
              background: 'var(--color-accent)',
              textTransform: 'capitalize',
            }}
          >
            {manifest.difficulty}
          </span>
          <span
            style={{
              fontSize: '0.75rem',
              padding: '2px 8px',
              borderRadius: 4,
              background: 'var(--color-accent)',
              textTransform: 'capitalize',
            }}
          >
            {manifest.duration}s
          </span>
          <span
            style={{
              fontSize: '0.75rem',
              padding: '2px 8px',
              borderRadius: 4,
              background: 'var(--color-accent)',
              textTransform: 'capitalize',
            }}
          >
            {manifest.runtime}
          </span>
          <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
            v{manifest.version}
          </span>
        </div>
      </div>

      {isLegacy ? (
        <div
          style={{
            textAlign: 'center',
            padding: '48px 0',
            background: 'var(--color-bg-secondary)',
            borderRadius: 'var(--radius)',
          }}
        >
          <p style={{ color: 'var(--color-text-muted)', marginBottom: 16 }}>
            This legacy game runs on the classic player.
          </p>
          <Link to={`/games/${manifest.slug}`} style={{ color: 'var(--color-primary)' }}>
            Open {manifest.title} &rarr;
          </Link>
        </div>
      ) : loadFailed ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <p style={{ color: 'var(--color-text-muted)' }}>
            Failed to load &quot;{manifest.title}&quot;. Please try again.
          </p>
        </div>
      ) : (
        <div
          ref={containerRef}
          data-testid="game-canvas"
          style={{
            width: '100%',
            maxWidth: 800,
            aspectRatio: '4 / 3',
            margin: '0 auto',
            borderRadius: 'var(--radius)',
            overflow: 'hidden',
            boxShadow: 'var(--shadow)',
            background: '#1a1a2e',
          }}
        />
      )}

      <div
        style={{
          marginTop: 24,
          padding: 16,
          background: 'var(--color-bg-secondary)',
          borderRadius: 'var(--radius)',
        }}
      >
        <h4 style={{ fontSize: '0.9rem', marginBottom: 8 }}>How to Play</h4>
        <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
          Targets appear at random positions. Click or tap them as fast as you can before the
          30-second timer runs out. Targets get smaller and appear more often as time passes.
          Clicking empty space does not cost points, but it costs time.
        </p>
      </div>
    </div>
  );
}
